/**
 * wt_jobs — well testing. GET /dashboard/wt-jobs (api#1261, WT-6): open jobs
 * with their counts by status, unacknowledged flags and late starts. "Late"
 * and every count are the API's; planned starts are calendar days.
 */
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../contexts/AuthContext';
import { WidgetFigure, WidgetPanel } from '../WidgetPanel';
import { useWtJobsWidget } from '../overviewQueries';
import { VerticalOff, OpsBadge, OpsRow, OpsWidgetRead, dash } from '../OpsWidgetRead';
import { wtJobStatusSkin } from '../../../lib/well-testing';
import { formatCalendarDate } from '../../../lib/calendar-date';
import type { WtJobs } from '../../../types/overview-operations';

function Body({ d }: { d: WtJobs }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="grid grid-cols-4 gap-3">
        <WidgetFigure testId="wtj-flowing" label="Flowing" value={d.counts.flowing} />
        <WidgetFigure testId="wtj-scheduled" label="Scheduled" value={d.counts.scheduled} />
        <WidgetFigure testId="wtj-flags" label="Open flags" value={d.counts.open_flags} alert={d.counts.open_flags > 0} />
        <WidgetFigure testId="wtj-late" label="Late" value={d.counts.late} alert={d.counts.late > 0} />
      </div>
      {d.jobs.length === 0 ? (
        <p data-testid="wtj-empty" className="mt-4 text-sm text-fieldiq-muted">No open well-test jobs.</p>
      ) : (
        <div data-testid="wtj-jobs" className="mt-4 divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
          {d.jobs.map((j) => {
            const skin = wtJobStatusSkin(j.status);
            return (
              <OpsRow key={j.id} to={j.link} onOpen={navigate} testId={`wtj-job-${j.id}`}>
                <span className="min-w-0">
                  <span className="block truncate font-semibold text-fieldiq-text">{dash(j.job_number)} · {dash(j.well_name)}</span>
                  <span className="block truncate text-fieldiq-muted">{dash(j.customer_name)} · {dash(j.supervisor_name)}</span>
                </span>
                <span className="flex shrink-0 flex-col items-end gap-1">
                  <span className="flex gap-1">
                    <OpsBadge fill={skin.fill}>{skin.label}</OpsBadge>
                    {typeof j.open_flags === 'number' && j.open_flags > 0 && (
                      <OpsBadge fill="bg-fi-signal-amber" testId="wtj-flags-badge">{`${j.open_flags} flag${j.open_flags === 1 ? '' : 's'}`}</OpsBadge>
                    )}
                    {j.late && <OpsBadge fill="bg-fi-signal-red" testId="wtj-late-badge">Late</OpsBadge>}
                  </span>
                  <span className="tabular-nums text-fieldiq-text">{formatCalendarDate(j.planned_start_on, { month: 'short', day: 'numeric' }) ?? '—'}</span>
                </span>
              </OpsRow>
            );
          })}
        </div>
      )}
    </>
  );
}

export default function WtJobsWidget() {
  const { hasVertical } = useAuth();
  const on = hasVertical('well_testing');
  const q = useWtJobsWidget(on);
  return (
    <WidgetPanel widgetKey="wt_jobs" eyebrow="Well testing" title="Well-Test Jobs" link={on ? { label: 'Jobs', to: '/well-testing/jobs' } : undefined}>
      <div className="p-4">
        {!on ? (
          <VerticalOff testId="wtj-vertical-off">Well testing is not on for this company.</VerticalOff>
        ) : (
          <OpsWidgetRead q={q} subject="well-test jobs" allowed="admins, office staff, supervisors, dispatchers and logistics">
            {(d) => <Body d={d} />}
          </OpsWidgetRead>
        )}
      </div>
    </WidgetPanel>
  );
}
